var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import React from 'react';
import classNames from 'classnames';
import styled from '@emotion/styled';
import { autobind } from '@typed-decorators/autobind';
import { getFocusableNodes } from '../../utils/focus';
/**
 * Renders a carousel object which is used to cycle through multiple elements,
 * usually used for jumbotron and banners.
 *
 * It provides auto play, dragging in both mobile and PC,
 * ability to render controls, and wrapping at the border of the carousel.
 */
export class Carousel extends React.PureComponent {
    constructor(props) {
        super(props);
        this.autoPlayTimer = null;
        this.touchStartPos = null;
        this.touchStartAt = 0;
        this.touchDelta = 0;
        this.touchTouches = [];
        this.touchTarget = null;
        this.touchScrolling = null;
        this.preventUntil = 0;
        this.node = null;
        this.listNode = null;
        this.listWrapNode = null;
        this.entryNodes = [];
        this.hadKeyEvent = false;
        this.state = {
            offset: props.initialOffset || 0,
            fromOffset: null,
            toOffset: null,
            offsetDelta: 0,
            animating: false,
            animatingDuration: '.35s',
            dragging: false,
            mainFocused: false,
            focused: false,
            focusVisible: false,
            autoPlaying: !!props.autoPlay,
        };
    }
    componentDidMount() {
        const { autoPlay } = this.props;
        this.toggleAutoPlayHandler(!!autoPlay);
        document.addEventListener('keydown', this.handleDocumentKeyDown, true);
        if (this.node != null) {
            this.node.addEventListener('touchstart', this.handleTouchStart, {
                passive: false,
            });
            this.node.addEventListener('touchmove', this.handleTouchMove, {
                passive: false,
            });
            this.node.addEventListener('focusin', this.handleMainFocusIn);
            this.node.addEventListener('focusout', this.handleMainFocusOut);
        }
        if (this.listNode != null) {
            this.listNode.addEventListener('focusin', this.handleFocusIn);
            this.listNode.addEventListener('focusout', this.handleFocusOut);
        }
    }
    componentDidUpdate() {
        const { autoPlaying, dragging } = this.state;
        // Disable auto play if it's dragging or autoPlay is disabled.
        this.toggleAutoPlayHandler(!!autoPlaying && !dragging);
    }
    componentWillUnmount() {
        this.toggleAutoPlayHandler(false);
        document.removeEventListener('keydown', this.handleDocumentKeyDown, true);
        if (this.node != null) {
            this.node.removeEventListener('touchstart', this.handleTouchStart);
            this.node.removeEventListener('touchmove', this.handleTouchMove);
            this.node.removeEventListener('focusin', this.handleMainFocusIn);
            this.node.removeEventListener('focusout', this.handleMainFocusOut);
        }
        if (this.listNode != null) {
            this.listNode.removeEventListener('focusin', this.handleFocusIn);
            this.listNode.removeEventListener('focusout', this.handleFocusOut);
        }
    }
    setAutoPlaying(enabled) {
        this.setState({ autoPlaying: enabled });
        this.toggleAutoPlayHandler(enabled);
    }
    toggleAutoPlayHandler(enabled) {
        const { autoPlayInterval } = this.props;
        if (enabled && this.autoPlayTimer == null) {
            this.autoPlayTimer = window.setInterval(this.next, autoPlayInterval || 3000);
        }
        else if (!enabled && this.autoPlayTimer != null) {
            clearInterval(this.autoPlayTimer);
            this.autoPlayTimer = null;
        }
    }
    handleTouchStart(e) {
        const { onInteract, disableSwipe, disableWrap } = this.props;
        const { offset } = this.state;
        const count = this.getLength();
        if (count <= 1) {
            // There's no point to perform dragging when count is lower than 2
            return;
        }
        if (disableSwipe) {
            return;
        }
        if (onInteract != null) {
            onInteract();
        }
        this.setState({ dragging: true });
        if (this.touchStartPos == null) {
            let newOffset = offset;
            if (disableWrap) {
                const bounds = this.getOffsetBounds();
                newOffset = Math.max(bounds[0], Math.min(bounds[1], newOffset));
            }
            this.touchStartPos = newOffset;
            this.touchStartAt = Date.now();
            this.touchScrolling = null;
            this.touchDelta = 0;
            if ('touches' in e) {
                this.touchTouches = Array.from(e.touches).map((touch) => ({
                    identifier: touch.identifier,
                    clientX: touch.clientX,
                    clientY: touch.clientY,
                }));
                // Attach touch events to the target.
                this.touchTarget = e.touches[0].target;
                this.touchTarget.addEventListener('touchmove', this.handleTouchMove);
                this.touchTarget.addEventListener('touchend', this.handleTouchEnd);
                this.touchTarget.addEventListener('touchcancel', this.handleTouchEnd);
                // Safari doesn't emit touchend in the touch target.
                window.addEventListener('touchend', this.handleTouchEnd);
                window.addEventListener('touchcancel', this.handleTouchEnd);
            }
            else {
                this.touchTouches = [
                    {
                        identifier: 0,
                        clientX: e.nativeEvent.clientX,
                        clientY: e.nativeEvent.clientY,
                    },
                ];
                // Attach touch events to the window.
                window.addEventListener('mousemove', this.handleTouchMove);
                window.addEventListener('mouseup', this.handleTouchEnd);
            }
            e.stopPropagation();
        }
    }
    handleTouchMove(e) {
        const { disableSwipe, disableWrap, childWidthRatio = 1 } = this.props;
        if (e.defaultPrevented)
            return;
        if (disableSwipe)
            return;
        if (this.node == null || this.touchStartPos == null)
            return;
        let touches = [];
        if ('touches' in e) {
            touches = Array.from(e.touches).map((v) => ({
                identifier: v.identifier,
                clientX: v.clientX,
                clientY: v.clientY,
            }));
        }
        else {
            touches = [
                {
                    identifier: 0,
                    clientX: e.clientX,
                    clientY: e.clientY,
                },
            ];
        }
        const { x: sumX, y: sumY, count } = touches
            .map((touch) => {
            const prev = this.touchTouches
                .find((v) => v.identifier === touch.identifier);
            if (prev == null)
                return null;
            return {
                x: touch.clientX - prev.clientX,
                y: touch.clientY - prev.clientY,
            };
        })
            .reduce((p, v) => (v != null
            ? ({ x: p.x + v.x, y: p.y + v.y, count: p.count + 1 })
            : p), { x: 0, y: 0, count: 0 });
        this.touchTouches = touches;
        if (count === 0)
            return;
        const diffX = sumX / count;
        const diffY = sumY / count;
        if ('touches' in e) {
            if (this.touchScrolling === null) {
                this.touchScrolling =
                    Math.abs(diffX) > Math.abs(diffY);
            }
            if (this.touchScrolling) {
                e.stopPropagation();
                e.preventDefault();
            }
            else {
                return;
            }
        }
        const { width } = this.node.getBoundingClientRect();
        this.touchDelta += diffX;
        const delta = this.touchDelta;
        let newOffset = this.touchStartPos - delta / width / childWidthRatio;
        let offsetDelta = 0;
        if (disableWrap) {
            const bounds = this.getOffsetBounds();
            // Apply offset bouncing effect if threshold is reached
            if (newOffset < bounds[0]) {
                offsetDelta = -Math.log(-newOffset + bounds[0] + 1) / Math.log(8);
            }
            else if (newOffset > bounds[1]) {
                offsetDelta = Math.log(newOffset - bounds[1] + 1) / Math.log(8);
            }
            newOffset = Math.max(bounds[0], Math.min(bounds[1], newOffset));
        }
        this.setState({
            offset: newOffset,
            offsetDelta,
            fromOffset: null,
            toOffset: null,
            animating: false,
            dragging: true,
        });
    }
    handleTouchEnd(e) {
        const { disableSwipe, childWidthRatio = 1, allowSwipeJump } = this.props;
        if (disableSwipe)
            return;
        if ('touches' in e && e.touches.length >= 1)
            return;
        if (this.node == null || this.touchStartPos == null)
            return;
        const { offset, dragging } = this.state;
        if (!dragging) {
            return;
        }
        const deltaX = this.touchDelta;
        const deltaTime = Date.now() - this.touchStartAt;
        let newOffset = Math.round(offset);
        const deltaOffset = newOffset - Math.round(this.touchStartPos);
        const { width } = this.node.getBoundingClientRect();
        const deltaValue = deltaX / deltaTime / width / childWidthRatio * 1000;
        let duration = '.35s';
        if (Math.abs(deltaValue) > 1) {
            const sign = deltaValue > 0 ? 1 : -1;
            let absDeltaValue = Math.abs(deltaValue);
            if (!allowSwipeJump) {
                absDeltaValue = 1;
            }
            const appliedDelta = Math.ceil(absDeltaValue / 3) * sign;
            if (Math.abs(deltaOffset) < Math.abs(appliedDelta)) {
                newOffset = Math.round(this.touchStartPos) - appliedDelta;
                duration = `${0.35 + Math.abs(appliedDelta) * 0.01}s cubic-bezier(.19,.18,.24,1)`;
            }
        }
        this.setState({ dragging: false });
        this.setOffset(newOffset, false, duration);
        // To prevent immediately firing click event, disable click event for
        // a short period if it moved far enough
        if (Math.abs(deltaX) > 5) {
            e.preventDefault();
            this.preventUntil = Date.now() + 200;
        }
        // Remove events if no more touches are left.
        if (!('touches' in e)) {
            this.touchStartPos = null;
            window.removeEventListener('mousemove', this.handleTouchMove);
            window.removeEventListener('mouseup', this.handleTouchEnd);
        }
        else if (e.touches.length === 0) {
            this.touchStartPos = null;
            if (this.touchTarget != null) {
                this.touchTarget.removeEventListener('touchmove', this.handleTouchMove);
                this.touchTarget.removeEventListener('touchend', this.handleTouchEnd);
                this.touchTarget.removeEventListener('touchcancel', this.handleTouchEnd);
            }
            window.removeEventListener('touchend', this.handleTouchEnd);
            window.removeEventListener('touchcancel', this.handleTouchEnd);
        }
    }
    handleClick(e) {
        if (Date.now() < this.preventUntil) {
            e.preventDefault();
            e.stopPropagation();
        }
    }
    handleDragStart(e) {
        e.preventDefault();
    }
    handleTransitionEnd() {
        const { autoPlay } = this.props;
        const { focused, toOffset, offset } = this.state;
        if (toOffset === offset) {
            this.setState({ fromOffset: null, toOffset: null, animating: false }, () => {
                if (focused && !autoPlay) {
                    // If we've had focus, set the focus to current node.
                    const { offset } = this.state;
                    this.focusNode(offset);
                }
            });
        }
    }
    handleMainFocusIn() {
        this.setState({ mainFocused: true });
        if (this.hadKeyEvent) {
            this.setState({ focusVisible: true });
        }
    }
    handleMainFocusOut() {
        this.setState({ mainFocused: false });
        const { toOffset } = this.state;
        if (toOffset == null) {
            this.setState({ focusVisible: false });
        }
    }
    handleFocusIn() {
        this.setState({ focused: true });
    }
    handleFocusOut() {
        this.setState({ focused: false });
    }
    handleKeyDown(e) {
        this.setState({ focusVisible: true });
        switch (e.keyCode) {
            case 37:
                // left
                this.prev();
                break;
            case 39:
                // right
                this.next();
                break;
            default:
        }
    }
    handleDocumentKeyDown() {
        this.hadKeyEvent = true;
        requestAnimationFrame(() => {
            this.hadKeyEvent = false;
        });
    }
    focusNode(offset) {
        const length = this.getLength();
        const newOffset = ((offset % length) + length) % length;
        const targetNode = this.entryNodes[newOffset];
        if (targetNode == null) {
            return;
        }
        const focusNodes = getFocusableNodes(targetNode);
        if (focusNodes.length > 0 && this.listWrapNode != null) {
            focusNodes[0].focus();
            this.listWrapNode.scrollLeft = 0;
        }
    }
    getLength() {
        const { children, length } = this.props;
        if (typeof children === 'function') {
            return length;
        }
        return React.Children.count(children);
    }
    getOffsetBounds() {
        const { childWidthRatio = 1, borderWidthRatio = 0, align = 'center', } = this.props;
        const length = this.getLength();
        if (align === 'center') {
            return [0, Math.max(0, length - 1)];
        }
        else if (align === 'left') {
            return [
                -borderWidthRatio,
                Math.max(0, length - 1 / childWidthRatio) + borderWidthRatio,
            ];
        }
        else if (align === 'centerConstrained') {
            const left = (1 / childWidthRatio - 1) / 2;
            return [
                left - borderWidthRatio,
                Math.max(0, length - 1 / childWidthRatio) + left + borderWidthRatio,
            ];
        }
        return [0, 0];
    }
    /**
     * Returns the "effective" bounds of the constrained layout. Effective bounds
     * is basically the meaningful bounds of the index.
     *
     * For example, if childWidthRatio is 1/2, and wrapping is disabled,
     * 0 and 1 position is effectively same, since 0 position can fully display
     * 1 position.
     *
     * The rightmost side is the same, length - 1, and length - 2 position
     * is redundant.
     *
     * If this redundant index is not resolved, the user have to press "next"
     * button twice to go to next page. Hence, this limits the bounds of the
     * min / max position. If the index is smaller / bigger than min / max,
     * it automatically changes to 0 / length - 1.
     *
     * @return {[number, number]} The min/max bounds of the offset.
     */
    getEffectiveBounds() {
        const { disableWrap, align, childWidthRatio = 1, } = this.props;
        const length = this.getLength();
        if (!disableWrap) {
            return [0, length - 1];
        }
        switch (align) {
            case 'centerConstrained':
                return [
                    Math.ceil((Math.floor(1 / childWidthRatio) - 1) / 2),
                    length - Math.ceil((Math.floor(1 / childWidthRatio) - 1) / 2) - 1,
                ];
            case 'left':
                return [
                    0,
                    length - Math.floor(1 / childWidthRatio),
                ];
            case 'center':
            default:
                return [0, length - 1];
        }
    }
    getEffectiveOffset(offset) {
        const { disableWrap } = this.props;
        if (disableWrap) {
            const bounds = this.getEffectiveBounds();
            return Math.max(bounds[0], Math.min(bounds[1], offset));
        }
        return offset;
    }
    setOffset(newOffset, wrap = true, inputDuration) {
        const { disableWrap, childWidthRatio = 1, animationDuration = '0.35s', } = this.props;
        const duration = inputDuration || animationDuration;
        const { focused, offset } = this.state;
        if (offset === newOffset) {
            // Bailout - do nothing if two offsets are same.
            return;
        }
        const length = this.getLength();
        this.setState((state) => {
            const wrappedOffset = wrap
                ? ((state.offset % length) + length) % length
                : state.offset;
            let fromOffset = wrappedOffset;
            let toOffset = typeof newOffset === 'function'
                ? newOffset(this.getEffectiveOffset(wrappedOffset))
                : newOffset;
            if (disableWrap) {
                const bounds = this.getOffsetBounds();
                fromOffset = Math.min(bounds[1], Math.max(bounds[0], fromOffset)) +
                    state.offsetDelta;
                const effectiveBounds = this.getEffectiveBounds();
                if (toOffset <= effectiveBounds[0])
                    toOffset = 0;
                if (toOffset >= effectiveBounds[1])
                    toOffset = length - 1;
            }
            // Optimization: If we're traveling too far distance, forcibly crop the
            // travel distance - otherwise the browser will draw too many nodes
            // at once.
            const travelDistance = Math.ceil(1 / childWidthRatio * 5);
            fromOffset = Math.max(toOffset - travelDistance, Math.min(toOffset + travelDistance, fromOffset));
            return {
                offset: fromOffset,
                fromOffset,
                toOffset,
                offsetDelta: state.offsetDelta,
                animating: false,
            };
        }, () => requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const { onOffsetChange, autoPlay } = this.props;
                const { toOffset } = this.state;
                const wrappedOffset = (((toOffset ?? 0) % length) + length) % length;
                if (toOffset != null) {
                    if (focused && !autoPlay) {
                        // If we've had focus, set the focus to current node.
                        this.focusNode(wrappedOffset);
                    }
                    if (onOffsetChange != null) {
                        onOffsetChange(wrappedOffset);
                    }
                    this.setState((state) => ({
                        offset: state.toOffset,
                        offsetDelta: 0,
                        animating: true,
                        animatingDuration: duration,
                    }));
                }
            });
        }));
    }
    prev(duration) {
        const { autoPlay, autoPlayInterval } = this.props;
        if (autoPlay && this.autoPlayTimer != null) {
            clearInterval(this.autoPlayTimer);
            this.autoPlayTimer = null;
            this.autoPlayTimer = window.setInterval(this.next, autoPlayInterval || 3000);
        }
        this.setOffset((offset) => Math.round(offset - 1), false, duration);
    }
    next(duration) {
        const { autoPlay, autoPlayInterval } = this.props;
        if (autoPlay && this.autoPlayTimer != null) {
            clearInterval(this.autoPlayTimer);
            this.autoPlayTimer = null;
            this.autoPlayTimer = window.setInterval(this.next, autoPlayInterval || 3000);
        }
        this.setOffset((offset) => Math.round(offset + 1), false, duration);
    }
    handleOnMouseEnter() {
        const { autoPlay } = this.props;
        if (autoPlay) {
            this.setAutoPlaying(false);
        }
    }
    handleOnMouseLeave() {
        const { autoPlay } = this.props;
        if (autoPlay) {
            this.setAutoPlaying(true);
        }
    }
    render() {
        const { children, length, className, wrapperClassName, listClassName, entryClassName, nodeRef, renderControls, childWidthRatio = 1, childLookahead = 0, disableWrap, wrappedChildren, onRowsRendered, align = 'center', } = this.props;
        let { offset, toOffset } = this.state;
        const { fromOffset, offsetDelta, animating, animatingDuration, autoPlaying, mainFocused, focused, focusVisible } = this.state;
        let childrenRender;
        let childrenLength;
        if (typeof children === 'function') {
            childrenRender = children;
            childrenLength = length;
        }
        else {
            const childrenList = React.Children.toArray(children);
            childrenRender = (index) => childrenList[index];
            childrenLength = childrenList.length;
        }
        const rawOffset = offset;
        if (disableWrap) {
            const bounds = this.getOffsetBounds();
            offset = Math.min(bounds[1], Math.max(bounds[0], offset)) + offsetDelta;
            if (toOffset != null) {
                toOffset = Math.min(bounds[1], Math.max(bounds[0], toOffset));
            }
        }
        let minOffset = offset;
        let maxOffset = offset;
        if (toOffset != null && fromOffset != null) {
            minOffset = Math.min(offset, fromOffset, toOffset);
            maxOffset = Math.max(offset, fromOffset, toOffset);
        }
        // Determine the required nodes to render current offset.
        const centerOffset = disableWrap
            ? Math.round(rawOffset)
            : ((Math.round(rawOffset) % childrenLength) + childrenLength) %
                childrenLength;
        let baseOffset;
        if (align === 'center' || align === 'centerConstrained') {
            // Wrap the offset
            const offsetMargin = (1 - childWidthRatio) / (2 * childWidthRatio);
            baseOffset = minOffset - offsetMargin - childLookahead;
        }
        else {
            baseOffset = minOffset - childLookahead;
        }
        // How many nodes should be visible for given offset?
        const startIndex = Math.floor(baseOffset);
        const visibleCount = Math.ceil(baseOffset +
            1 / childWidthRatio +
            (maxOffset - minOffset) +
            childLookahead * 2) -
            startIndex;
        // Then.. how much should we move left?
        const leftOffset = (baseOffset - startIndex + (offset - minOffset) + childLookahead) *
            childWidthRatio;
        const effectiveBounds = this.getEffectiveBounds();
        const controlProps = {
            prev: this.prev,
            next: this.next,
            set: this.setOffset,
            offset: this.getEffectiveOffset(centerOffset),
            length: childrenLength,
            min: effectiveBounds[0],
            max: effectiveBounds[1],
            animating: animating || toOffset != null,
        };
        // onRowsRendered is called in render phase.
        // TODO Refactor this.
        if (onRowsRendered != null) {
            onRowsRendered(startIndex, startIndex + visibleCount - 1);
        }
        const nodes = [];
        for (let i = 0; i < visibleCount; ++i) {
            const actualIndex = startIndex + i;
            const index = ((startIndex % childrenLength) + childrenLength + i) % childrenLength;
            const epoch = Math.floor(((startIndex % childrenLength) + childrenLength + i) / childrenLength);
            const isWrapped = (actualIndex <= -1 || actualIndex >= childrenLength);
            nodes.push(React.createElement(CarouselListItem, { className: classNames('carousel__list__entry', epoch !== 1 && disableWrap && 'wrapped', focused && 'focus', focusVisible && 'focus-visible', entryClassName), key: actualIndex, style: { width: `${childWidthRatio * 100}%` }, ref: (node) => {
                    this.entryNodes[index] = node;
                }, onMouseEnter: this.handleOnMouseEnter, onMouseLeave: this.handleOnMouseLeave, role: 'group', "aria-roledescription": 'slide', "aria-label": isWrapped && disableWrap
                    ? undefined
                    : (`${index + 1} of ${childrenLength}`) }, isWrapped && disableWrap
                // If disableWrap is true and epoch has exceeded, don't render
                // anything but leftmost / rightmost entry.
                ? ((actualIndex === -1 || actualIndex === childrenLength) &&
                    wrappedChildren != null && wrappedChildren(actualIndex))
                : childrenRender(index, actualIndex, controlProps)));
        }
        return (React.createElement(CarouselContainer, { className: classNames((mainFocused || focused) && 'focus', focused && 'focus-entry', focusVisible && 'focus-visible', className), ref: (node) => {
                this.node = node;
                if (nodeRef != null) {
                    nodeRef(node);
                }
            }, role: 'region', "aria-roledescription": 'carousel' },
            React.createElement(CarouselListWrapper, { className: classNames('carousel__list-wrap', wrapperClassName), ref: (node) => {
                    this.listWrapNode = node;
                } },
                React.createElement(CarouselList, { className: classNames('carousel__list', listClassName), onTouchMove: () => { }, onTouchEnd: () => { }, onMouseDown: this.handleTouchStart, onClickCapture: this.handleClick, onDragStart: this.handleDragStart, onTransitionEnd: this.handleTransitionEnd, onKeyDown: this.handleKeyDown, "aria-live": autoPlaying ? 'off' : 'polite', ref: (node) => {
                        this.listNode = node;
                    }, style: {
                        transform: `translateX(${leftOffset * -100}%)`,
                        transition: animating
                            ? `transform ${animatingDuration}`
                            : '',
                    } }, nodes)),
            renderControls != null &&
                renderControls(controlProps)));
    }
}
__decorate([
    autobind
], Carousel.prototype, "handleTouchStart", null);
__decorate([
    autobind
], Carousel.prototype, "handleTouchMove", null);
__decorate([
    autobind
], Carousel.prototype, "handleTouchEnd", null);
__decorate([
    autobind
], Carousel.prototype, "handleClick", null);
__decorate([
    autobind
], Carousel.prototype, "handleDragStart", null);
__decorate([
    autobind
], Carousel.prototype, "handleTransitionEnd", null);
__decorate([
    autobind
], Carousel.prototype, "handleMainFocusIn", null);
__decorate([
    autobind
], Carousel.prototype, "handleMainFocusOut", null);
__decorate([
    autobind
], Carousel.prototype, "handleFocusIn", null);
__decorate([
    autobind
], Carousel.prototype, "handleFocusOut", null);
__decorate([
    autobind
], Carousel.prototype, "handleKeyDown", null);
__decorate([
    autobind
], Carousel.prototype, "handleDocumentKeyDown", null);
__decorate([
    autobind
], Carousel.prototype, "setOffset", null);
__decorate([
    autobind
], Carousel.prototype, "prev", null);
__decorate([
    autobind
], Carousel.prototype, "next", null);
__decorate([
    autobind
], Carousel.prototype, "handleOnMouseEnter", null);
__decorate([
    autobind
], Carousel.prototype, "handleOnMouseLeave", null);
const CarouselContainer = styled.div `
  position: relative;
`;
const CarouselListWrapper = styled.div `
  overflow: hidden;
`;
const CarouselList = styled.div `
  display: flex;
  flex-wrap: nowrap;
  align-items: stretch;
  min-height: 100%;
`;
const CarouselListItem = styled.div `
  flex: 0 0 auto;
  -moz-user-select: -moz-none;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
  box-sizing: border-box;
`;
//# sourceMappingURL=Carousel.js.map